import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

export const listCashRegistersWithOpenSessionSkill: Skill = {
  id: "listCashRegistersWithOpenSession",
  description:
    "Lista cajas registradoras del restaurante indicando cuáles tienen sesión abierta. Útil ANTES de openCashSession o closeOrder.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Verificando cajas activas…",
      successMsg: "✓ Cajas verificadas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/cash-registers/with-open-session`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          const registers = items.map((r) => ({
            id: r.id,
            name: r.name,
            hasOpenSession: !!r.openSession,
            sessionId: r.openSession?.id ?? null,
            openedAt: r.openSession?.openedAt ?? null,
          }));
          return {
            ok: true as const,
            result: { count: registers.length, registers },
            humanSummary: `${registers.length} caja(s) registradora(s).`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const openCashSessionSkill: Skill = {
  id: "openCashSession",
  description:
    "Abre la sesión de caja del día (apertura de caja). Requiere cashRegisterId. Si no lo conoces, usa listCashRegistersWithOpenSession primero.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      cashRegisterId: { type: "string" },
      openingBalance: { type: "number" },
      openingNotes: { type: "string" },
    },
    required: ["restaurantId", "cashRegisterId", "openingBalance"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    const openingBalance = Number(args.openingBalance);
    if (!restaurantId || !cashRegisterId || !Number.isFinite(openingBalance) || openingBalance < 0) {
      return { ok: false, error: "Datos inválidos." };
    }
    const openingNotes = args.openingNotes ? String(args.openingNotes) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Abrir caja del día",
      description: "Vas a iniciar la sesión de caja con el saldo inicial indicado.",
      details: [
        { label: "Saldo inicial", value: `$${openingBalance.toLocaleString()}` },
        ...(openingNotes ? [{ label: "Notas", value: openingNotes }] : []),
      ],
      confirmLabel: "Abrir caja",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Abriendo caja del día…",
      successMsg: `✓ Caja abierta con $${openingBalance.toLocaleString()}`,
      restaurantId,
      targetTab: "sesiones-caja",
      targetTabLabel: "Apertura / Cierre",
      run: async () => {
        try {
          const body: Record<string, unknown> = { openingBalance };
          if (openingNotes) body.openingNotes = openingNotes;
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/cash-registers/${cashRegisterId}/sessions/open`,
            body,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "orders", action: "created", restaurantId, id: data?.id });
          return {
            ok: true as const,
            result: { sessionId: data?.id, openingBalance },
            humanSummary: `Caja abierta con $${openingBalance.toLocaleString()}.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const closeCashSessionSkill: Skill = {
  id: "closeCashSession",
  description:
    "Cierra la sesión de caja activa. Compara efectivo contado vs esperado y registra diferencia. IRREVERSIBLE.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      cashRegisterId: { type: "string" },
      sessionId: { type: "string" },
      countedCash: { type: "number" },
      closingNotes: { type: "string" },
    },
    required: ["restaurantId", "cashRegisterId", "sessionId", "countedCash"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    const sessionId = String(args.sessionId ?? "");
    const countedCash = Number(args.countedCash);
    if (!restaurantId || !cashRegisterId || !sessionId || !Number.isFinite(countedCash) || countedCash < 0) {
      return { ok: false, error: "Datos inválidos." };
    }
    const closingNotes = args.closingNotes ? String(args.closingNotes) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Cerrar caja del día",
      description: "Vas a CERRAR la sesión de caja. La acción es IRREVERSIBLE.",
      details: [
        { label: "Efectivo contado", value: `$${countedCash.toLocaleString()}` },
        ...(closingNotes ? [{ label: "Notas", value: closingNotes }] : []),
      ],
      confirmLabel: "Cerrar caja",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Cerrando caja del día…",
      successMsg: "✓ Caja cerrada",
      restaurantId,
      targetTab: "sesiones-caja",
      targetTabLabel: "Apertura / Cierre",
      run: async () => {
        try {
          const body: Record<string, unknown> = { countedCash };
          if (closingNotes) body.closingNotes = closingNotes;
          const { data } = await apiWithAuth.patch(
            `/restaurants/${restaurantId}/cash-registers/${cashRegisterId}/sessions/${sessionId}/close`,
            body,
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: {
              sessionId,
              countedCash,
              difference: data?.difference ?? null,
              totalSales: data?.totalSales ?? null,
            },
            humanSummary: `Caja cerrada. Diferencia: $${(data?.difference ?? 0).toLocaleString()}.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
